import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import useAdminData from "../admin/useAdminData";
import useAdminHandlers from "../admin/useAdminHandlers";
import UserManagement, { EditUserModal } from "../admin/UserManagement";
import ResetPasswordModal from "../admin/ResetPasswordModal";
import { logout } from "../../store/authSlice";
import { findUserRoleConfig, USER_ROLE_ROUTE_CONFIG } from "./userRoleConfig";
import {
  getCreatableRoles,
  getManageableRoles,
  hasUserPermission,
  normalizeRole,
  ROLES,
} from "../../utils/rbacHelper";
import "../admin/dashboard-redesign.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const toIdString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const VISIBLE_USER_ROLE_SET = new Set(
  USER_ROLE_ROUTE_CONFIG.map((item) => normalizeRole(item.role))
);

export default function UsersRolesPage({ section = "manager" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { section: routeSection } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabelText = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const roleConfig = findUserRoleConfig(routeSection || section);
  const currentUserRole = normalizeRole(currentUser?.role);
  const currentUserId = toIdString(currentUser?._id || currentUser?.id);
  const manageableRoles = getManageableRoles(currentUserRole);
  const visibleRoleRoutes = useMemo(() => {
    if (currentUserRole === ROLES.SUPER_ADMIN) return USER_ROLE_ROUTE_CONFIG;
    const manageableRoleSet = new Set(manageableRoles.map((role) => normalizeRole(role)));
    return USER_ROLE_ROUTE_CONFIG.filter((item) => manageableRoleSet.has(normalizeRole(item.role)));
  }, [currentUserRole, manageableRoles]);
  const creatableRoles = useMemo(
    () =>
      getCreatableRoles(currentUserRole).filter((role) =>
        VISIBLE_USER_ROLE_SET.has(normalizeRole(role))
      ),
    [currentUserRole]
  );

  const audioRef = useRef(null);
  const prevEventsRef = useRef();
  const didFetchRef = useRef(false);

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const [editingAsset, setEditingAsset] = useState(null);
  const [newAssetData, setNewAssetData] = useState({});
  const [editingDevice, setEditingDevice] = useState(null);
  const [, setShowEditDeviceModal] = useState(false);
  const [selectedDeviceForGeofence, setSelectedDeviceForGeofence] = useState(null);
  const [unsavedGeofence, setUnsavedGeofence] = useState(null);
  const [geofenceName, setGeofenceName] = useState("");
  const [, setRouteStart] = useState(null);
  const [, setRouteEnd] = useState(null);
  const [, setRouteDetails] = useState(null);
  const [, setLocationError] = useState("");
  const [, setShowLocationError] = useState(false);
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [assignToUserId, setAssignToUserId] = useState("");
  const [assignUserToGroupAdminUserId, setAssignUserToGroupAdminUserId] = useState("");
  const [assignUserToGroupAdminTargetId, setAssignUserToGroupAdminTargetId] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [assignUserSearchQuery, setAssignUserSearchQuery] = useState("");
  const [groupAdminSearchQuery, setGroupAdminSearchQuery] = useState("");
  const [, setShowCreateUserModal] = useState(false);

  useEffect(() => {
    if (!visibleRoleRoutes.length) return;
    const currentSectionAllowed = visibleRoleRoutes.some((item) => item.key === roleConfig.key);
    if (currentSectionAllowed) return;
    navigate(visibleRoleRoutes[0].path, { replace: true });
  }, [navigate, roleConfig.key, visibleRoleRoutes]);

  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  const {
    users,
    devices,
    editingUser,
    setEditingUser,
    fetchAdminData,
  } = useAdminData({
    currentUser,
    handleLogout,
    prevEventsRef,
    didFetchRef,
    setSelectedDevices,
    activeTab: "users",
    selectedDevices,
    audioRef,
    skipFetch: !currentUser,
  });

  const handlers = useAdminHandlers({
    currentUser,
    handleLogout,
    resetUserId,
    newPassword: newPassword,
    confirmPassword,
    setNewPassword,
    setConfirmPassword,
    setShowResetPasswordModal,
    editingAsset,
    setEditingAsset,
    setShowEditAssetModal: () => {},
    newAssetData,
    setNewAssetData,
    setShowAddAssetModal: () => {},
    assignDeviceId,
    assignToUserId,
    setAssignDeviceId,
    setAssignToUserId,
    assignUserToGroupAdminUserId,
    assignUserToGroupAdminTargetId,
    setAssignUserToGroupAdminUserId,
    setAssignUserToGroupAdminTargetId,
    editingUser,
    setEditingUser,
    editingDevice,
    setEditingDevice,
    setShowEditDeviceModal,
    setShowAddDeviceModal: () => {},
    selectedDeviceForGeofence,
    setSelectedDeviceForGeofence,
    unsavedGeofence,
    geofenceName,
    setShowGeofenceModal: () => {},
    setUnsavedGeofence,
    setGeofenceName,
    setRouteStart,
    setRouteEnd,
    setRouteDetails,
    setLocationError,
    setShowLocationError,
    fetchAdminData,
    devices,
  });

  const {
    handleResetPassword,
    handleAssignDevice,
    handleAssignUserToGroupAdmin,
    handleRemoveManagedUser,
    handleDeleteUser,
    handleRestoreUser,
    handleLoginAsUser,
  } = handlers;

  const safeUsers = Array.isArray(users) ? users : [];
  const safeDevices = Array.isArray(devices) ? devices : [];

  const scopedUsers = useMemo(() => {
    return safeUsers.filter((user) => {
      const role = normalizeRole(user.role);
      const userId = toIdString(user?._id);
      const createdBy = toIdString(user?.createdBy);
      const dealerOwner = toIdString(user?.dealerOwner);
      const managedUserIds = (currentUser?.managedUsers || []).map((managedUser) =>
        toIdString(managedUser?._id || managedUser)
      );

      if (currentUserRole === ROLES.SUPER_ADMIN) return role !== ROLES.SUPER_ADMIN;
      if ([ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE, ROLES.HR].includes(currentUserRole)) {
        return manageableRoles.includes(role);
      }

      return (
        userId === currentUserId ||
        (manageableRoles.includes(role) &&
          (createdBy === currentUserId ||
            dealerOwner === currentUserId ||
            managedUserIds.includes(userId)))
      );
    });
  }, [currentUser?.managedUsers, currentUserId, currentUserRole, manageableRoles, safeUsers]);

  const assignableUsersForDevices = useMemo(
    () => scopedUsers.filter((user) => manageableRoles.includes(normalizeRole(user.role))),
    [manageableRoles, scopedUsers]
  );

  const assignableUsersForGroupAdmin = useMemo(() => {
    return safeUsers.filter((user) => {
      if ([ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.SUB_ADMIN, ROLES.DEALER].includes(currentUserRole)) {
        return [ROLES.USER, ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER].includes(normalizeRole(user.role));
      }
      return false;
    });
  }, [currentUserRole, safeUsers]);

  const groupAdmins = useMemo(() => {
    return safeUsers.filter((user) => {
      if ([ROLES.SUPER_ADMIN, ROLES.MANAGER].includes(currentUserRole)) {
        return [ROLES.SUB_ADMIN, ROLES.DEALER].includes(normalizeRole(user.role));
      }
      if (currentUserRole === ROLES.OFFICE_EMPLOYEE) {
        return normalizeRole(user.role) === ROLES.SUB_ADMIN;
      }
      if (currentUserRole === ROLES.SUB_ADMIN) {
        return normalizeRole(user.role) === ROLES.DEALER;
      }
      return false;
    });
  }, [currentUserRole, safeUsers]);

  const assignmentLabels = useMemo(
    () => ({
      userLabel: "Select User",
      adminLabel:
        currentUserRole === ROLES.OFFICE_EMPLOYEE
          ? "Select Sub-Admin"
          : currentUserRole === ROLES.SUB_ADMIN
            ? "Select Dealer"
            : "Select Admin/Dealer",
      adminPlaceholder:
        currentUserRole === ROLES.OFFICE_EMPLOYEE
          ? "Sub-Admin Name"
          : currentUserRole === ROLES.SUB_ADMIN
            ? "Dealer Name"
            : "Sub-Admin or Dealer Name",
    }),
    [currentUserRole]
  );

  const sidebarItemsLegacy = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/dashboard", onClick: () => navigate("/dashboard") },
      { key: "vehicles", label: "Vehicles", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"), onClick: () => navigate("/admin-dashboard?tab=devices") },
      { key: "live-tracking", label: "Live Tracking", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"), onClick: () => navigate("/admin-dashboard?tab=map") },
      { key: "trips", label: "Trips", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
      { key: "geofences", label: "Geofences", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/geofences", onClick: () => navigate("/geofences") },
      { key: "notifications", label: "Notifications", icon: <NotificationsActiveOutlinedIcon fontSize="small" />, active: location.pathname === "/notifications", onClick: () => navigate("/notifications") },
      { key: "reports", label: "Reports", icon: <ReceiptLongOutlinedIcon fontSize="small" />, active: location.pathname === "/reports", onClick: () => navigate("/reports") },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      { key: "groups", label: "Groups", icon: <Groups2OutlinedIcon fontSize="small" />, active: location.pathname.startsWith("/groups"), onClick: () => navigate("/groups") },
      { key: "reminders", label: "Reminders", icon: <EventNoteOutlinedIcon fontSize="small" />, active: location.pathname === "/reminders", onClick: () => navigate("/reminders") },
      { key: "routes", label: "Routes", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/routes", onClick: () => navigate("/routes") },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        defaultExpanded: true,
        children: visibleRoleRoutes.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      { key: "billing", label: "Billing & Licenses", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/licenses", onClick: () => navigate("/licenses") },
      { key: "settings", label: "Settings", icon: <SettingsOutlinedIcon fontSize="small" />, active: location.pathname === "/profile", onClick: () => navigate("/profile") },
    ],
    [location.pathname, location.search, navigate, visibleRoleRoutes]
  );
  const sidebarItems = useGlobalSidebarItems();
  void sidebarItemsLegacy;

  if (!currentUser) {
    return null;
  }

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabelText}
        onSupport={() => navigate("/message")}
        onLogout={handleLogout}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <UserManagement
          users={scopedUsers}
          creatableRoles={creatableRoles}
          manageableRoles={manageableRoles}
          currentUser={currentUser}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          setEditingUser={setEditingUser}
          handleDeleteUser={handleDeleteUser}
          handleRestoreUser={handleRestoreUser}
          handleRemoveManagedUser={handleRemoveManagedUser}
          setResetUserId={setResetUserId}
          setShowResetPasswordModal={setShowResetPasswordModal}
          setShowCreateUserModal={setShowCreateUserModal}
          canAssignDevice={hasUserPermission(currentUser, "createDevice")}
          canAssignUserToGA={hasUserPermission(currentUser, "assignUserToGA")}
          assignmentLabels={assignmentLabels}
          handleAssignDevice={handleAssignDevice}
          handleAssignUserToGroupAdmin={handleAssignUserToGroupAdmin}
          safeDevices={safeDevices}
          assignableUsersForDevices={assignableUsersForDevices}
          assignableUsersForGroupAdmin={assignableUsersForGroupAdmin}
          groupAdmins={groupAdmins}
          deviceSearchQuery={deviceSearchQuery}
          setDeviceSearchQuery={setDeviceSearchQuery}
          assignDeviceId={assignDeviceId}
          setAssignDeviceId={setAssignDeviceId}
          userSearchQuery={userSearchQuery}
          setUserSearchQuery={setUserSearchQuery}
          assignToUserId={assignToUserId}
          setAssignToUserId={setAssignToUserId}
          assignUserSearchQuery={assignUserSearchQuery}
          setAssignUserSearchQuery={setAssignUserSearchQuery}
          assignUserToGroupAdminUserId={assignUserToGroupAdminUserId}
          setAssignUserToGroupAdminUserId={setAssignUserToGroupAdminUserId}
          groupAdminSearchQuery={groupAdminSearchQuery}
          setGroupAdminSearchQuery={setGroupAdminSearchQuery}
          assignUserToGroupAdminTargetId={assignUserToGroupAdminTargetId}
          setAssignUserToGroupAdminTargetId={setAssignUserToGroupAdminTargetId}
          onRefreshData={fetchAdminData}
          forcedRole={roleConfig.role}
          pageTitle={roleConfig.pageTitle}
          pageSubtitle={roleConfig.pageSubtitle}
          pageBreadcrumb={`Home > Users & Roles > ${roleConfig.label}`}
          addButtonLabel={roleConfig.addLabel}
          directCreateRole={roleConfig.role}
          bucketLabel="Department"
          entityPluralLabel={roleConfig.pluralLabel}
          rolePageMode
        />

        {editingUser ? (
          <EditUserModal
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            handleEditUser={handlers.handleEditUser}
            currentUser={currentUser}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            creatableRoles={creatableRoles}
          />
        ) : null}

        {showResetPasswordModal ? (
          <ResetPasswordModal
            handleResetPassword={handleResetPassword}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            setShowResetPasswordModal={setShowResetPasswordModal}
          />
        ) : null}
      </main>
    </div>
  );
}
